import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { StyledMenu } from "../../../StyledMenu.jsx";
import {
  DEFAULT_SELECTED_FILTERS,
  FILTER_TYPES,
} from "../AppointmentHistoryCard.jsx";

const LABELS = (t) => ({
  [FILTER_TYPES.APPOINTMENT]: t("client.appointmentEntry"),
  [FILTER_TYPES.APPOINTMENT_EVENTS]: t(
    "client.appointmentEvents",
    "Události termínů",
  ),
  [FILTER_TYPES.MEDICAL_REPORT]: t("client.medicalReportLegacy"),
  [FILTER_TYPES.NEXT_APPOINTMENT_NOTE]: t("client.nextAppointmentNote"),
  [FILTER_TYPES.RECEPTION_NOTE]: t("client.receptionNote", "Zápis pro recepci"),
  [FILTER_TYPES.FLOOR_MANAGER_NOTE]: t(
    "client.floorManagerNote",
    "Poznámka pro FM",
  ),
  [FILTER_TYPES.CLIENT_NOTE]: t("client.otherNotes", "Poznámka do osy"),
  [FILTER_TYPES.FILE_EVENT]: t("Dokumenty"),
  [FILTER_TYPES.HISTORY_RECORD]: t("client.historyRecord", "Záznam v historii"),
});

export function FilterMenu({
  selected = [],
  onChange,
  canSeeAppointmentEvents,
  canSeeNoteForFloorManager,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const labels = React.useMemo(() => LABELS(t), [t]);

  const allOptions = React.useMemo(() => {
    const base = [
      FILTER_TYPES.APPOINTMENT,
      FILTER_TYPES.MEDICAL_REPORT,
      FILTER_TYPES.NEXT_APPOINTMENT_NOTE,
      FILTER_TYPES.CLIENT_NOTE,
      FILTER_TYPES.RECEPTION_NOTE,
      FILTER_TYPES.FLOOR_MANAGER_NOTE,
      FILTER_TYPES.APPOINTMENT_EVENTS,
      FILTER_TYPES.FILE_EVENT,
      FILTER_TYPES.HISTORY_RECORD,
      FILTER_TYPES.WEBHOOK,
    ];
    return base.filter((opt) => {
      if (!canSeeAppointmentEvents && opt === FILTER_TYPES.APPOINTMENT_EVENTS)
        return false;
      if (!canSeeNoteForFloorManager && opt === FILTER_TYPES.FLOOR_MANAGER_NOTE)
        return false;
      return true;
    });
  }, [canSeeAppointmentEvents, canSeeNoteForFloorManager]);

  const toggle = (value) => {
    const set = new Set(selected);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onChange(Array.from(set));
  };

  const revertToDefault = () => onChange(DEFAULT_SELECTED_FILTERS);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Button
        id="filter-menu-button"
        aria-controls={open ? "filter-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{ display: "flex" }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant="outlined"
        startIcon={<FontAwesomeIcon icon={faFilter} />}
      >
        {t("client.filter", "Filtr")}
      </Button>

      <StyledMenu
        id="filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-labelledby": "filter-menu-button" }}
      >
        <Typography variant="h3" sx={{ px: 2, py: 1 }}>
          Filtrovat historii
        </Typography>
        <Divider />
        {allOptions.map((opt) => (
          <MenuItem key={opt} onClick={() => toggle(opt)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                tabIndex={-1}
                disableRipple
                checked={selected.includes(opt)}
              />
            </ListItemIcon>
            <ListItemText primary={labels[opt] || opt} />
          </MenuItem>
        ))}
        <Divider />
        <Box
          sx={{ display: "flex", px: 2, pb: 1, justifyItems: "space-between" }}
        >
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => onChange([])}
          >
            {t("common.cancel", "Zrušit filtry")}
          </Button>
          <Button variant="contained" size="small" onClick={revertToDefault}>
            {t("common.default", "Výchozí")}
          </Button>
        </Box>
      </StyledMenu>
    </Box>
  );
}

export default FilterMenu;
